import React, { useEffect, useRef, useState } from 'react';
import PipelineShell from '../common/PipelineShell';
import {
  getProject,
  patchStage,
  subscribe,
  buildStageContext,
  getAwsConfig,
} from '../../../services/pipelineProject.service';
import { triggerDeployment, getDeploymentStatus } from '../../../services/pipeline.service';
import '../Pipeline.css';

/**
 * Deploy Stage
 *
 * Shows a deployment readiness checklist, a summary of what will be deployed,
 * and a prominent "Proceed with Deployment to AWS" button.
 *
 * On deploy:
 *  1. Calls POST /pipeline/deploy with the full pipeline context.
 *  2. Polls the returned job until it completes or fails.
 *  3. Shows live status + deployment outputs (URLs, ARNs, stack name).
 */
function DeployStage() {
  const [project, setProject]       = useState(getProject());
  const [deploying, setDeploying]   = useState(false);
  const [deployStatus, setDeployStatus] = useState(null);
  const [deployOutput, setDeployOutput] = useState(null);
  const [deployError, setDeployError]   = useState(null);
  const [deployMeta, setDeployMeta]     = useState(null); // {stack_name, region}
  const pollRef  = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return subscribe(setProject);
  }, []);

  useEffect(() => {
    return () => {
      aliveRef.current = false;
      clearInterval(pollRef.current);
    };
  }, []);

  const pollDeployJob = (jobId) => {
    const poll = async () => {
      try {
        const job = await getDeploymentStatus(jobId);
        if (!aliveRef.current) return;
        const st = job.status || (job.result && job.result.status);
        if (st === 'success' || st === 'completed') {
          clearInterval(pollRef.current);
          setDeploying(false);
          setDeployStatus('success');
          const out = job.result?.outputs || job.outputs || job.result || {};
          setDeployOutput(out);
          patchStage('deployment', { data: { status: 'deployed', outputs: out } });
        } else if (st === 'failed' || st === 'error') {
          clearInterval(pollRef.current);
          setDeploying(false);
          setDeployStatus('error');
          setDeployError(job.result?.error || job.error || 'Deployment failed');
        }
      } catch {
        // transient — keep polling
      }
    };
    pollRef.current = setInterval(poll, 12000);
  };

  const handleDeploy = async () => {
    setDeployError(null);
    setDeployOutput(null);
    setDeployMeta(null);
    setDeploying(true);
    setDeployStatus('deploying');

    const context    = buildStageContext('deployment');
    const awsConfig  = getAwsConfig();
    const payload    = {
      ...context,
      awsConfig,
      requirements: project?.stages?.requirements?.data || {},
      design:       project?.stages?.design?.data || {},
      docs:         project?.stages?.docs?.data || {},
      code:         project?.stages?.code?.data || {},
      cicd:         project?.stages?.cicd?.data || {},
    };

    try {
      const res = await triggerDeployment(payload);
      if (res.job_id && res.status === 'deploying') {
        setDeployMeta({ stack_name: res.stack_name, region: res.region });
        patchStage('deployment', { jobId: res.job_id, data: { status: 'deploying' } });
        pollDeployJob(res.job_id);
      } else {
        // Synchronous / mock response
        setDeploying(false);
        setDeployStatus('success');
        const out = res.outputs || res.result || res;
        setDeployOutput(out);
        patchStage('deployment', { data: { status: 'deployed', outputs: out } });
      }
    } catch (err) {
      setDeploying(false);
      setDeployStatus('error');
      setDeployError(err.response?.data?.error || err.message || 'Deployment failed');
    }
  };

  const cicd        = project?.stages?.cicd?.data;
  const requirements = project?.stages?.requirements?.data;
  const code        = project?.stages?.code?.data;

  // Readiness checks
  const checks = [
    { label: 'Requirements confirmed',  ok: Boolean(project?.stages?.requirements?.approved) },
    { label: 'Design approved',         ok: Boolean(project?.stages?.design?.approved) },
    { label: 'Documentation approved',  ok: Boolean(project?.stages?.docs?.approved) },
    { label: 'Code generated & approved', ok: Boolean(project?.stages?.code?.approved) },
    { label: 'Tests approved',          ok: Boolean(project?.stages?.tests?.approved) },
    { label: 'CI/CD config approved',   ok: Boolean(project?.stages?.cicd?.approved) },
  ];
  const allReady = checks.every((c) => c.ok);

  const totalFiles = (code?.files?.length || 0) + (cicd?.files?.length || 0);

  return (
    <PipelineShell
      stageId="deployment"
      description="Review the deployment readiness checklist, then deploy to AWS."
      canApprove={deployStatus === 'success'}
    >
      {/* ── Readiness Checklist ──────────────────────────────────────── */}
      <div className="pipeline-card">
        <div className="pipeline-card-title">Deployment Readiness</div>
        <ul className="pipeline-checklist">
          {checks.map((c, i) => (
            <li key={i}>
              <span className={c.ok ? 'pipeline-check-icon' : 'pipeline-warn-icon'}>
                {c.ok ? '✅' : '⚠️'}
              </span>
              <span style={{ color: c.ok ? '#166534' : '#92400e' }}>{c.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Deployment Summary ───────────────────────────────────────── */}
      <div className="pipeline-deploy-summary">
        <div className="pipeline-deploy-title">
          🚀 {requirements?.title || 'Your Project'} — AWS Deployment
        </div>
        <p style={{ margin: '6px 0 12px', fontSize: '0.88rem', color: '#475569' }}>
          {requirements?.overview || 'Ready to deploy your generated application to AWS.'}
        </p>
        <div className="pipeline-tag-row">
          {totalFiles > 0 && <span className="pipeline-tag">{totalFiles} files</span>}
          {cicd?.deploymentStrategy && <span className="pipeline-tag">{cicd.deploymentStrategy}</span>}
          {Array.isArray(cicd?.environments) && cicd.environments.map((e) => (
            <span key={e} className="pipeline-tag green">{e}</span>
          ))}
        </div>
      </div>

      {/* ── Deploy Button ────────────────────────────────────────────── */}
      {deployStatus !== 'success' && (
        <button
          className="pipeline-deploy-btn"
          onClick={handleDeploy}
          disabled={deploying || !allReady}
          title={!allReady ? 'Approve all previous stages before deploying' : undefined}
        >
          {deploying ? '⏳ Deploying to AWS…' : '🚀 Proceed with Deployment to AWS'}
        </button>
      )}

      {!allReady && !deploying && (
        <p style={{ textAlign: 'center', fontSize: '0.83rem', color: '#f59e0b', marginTop: 8 }}>
          ⚠️ Some stages have not been approved yet. Approve all stages before deploying.
        </p>
      )}

      {/* ── Status ───────────────────────────────────────────────────── */}
      {deployStatus === 'deploying' && (
        <div className="pipeline-deploy-status deploying">
          <div className="pipeline-spinner" style={{ width: 20, height: 20, display: 'inline-block', verticalAlign: 'middle', marginRight: 10 }} />
          Deploying to AWS via CloudFormation…{' '}
          {deployMeta && (
            <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', marginLeft: 8 }}>
              Stack: <b>{deployMeta.stack_name}</b> · Region: <b>{deployMeta.region}</b>
            </span>
          )}
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 6 }}>
            This may take 3–10 minutes. You can safely leave this page and return.
          </div>
        </div>
      )}

      {deployError && (
        <div className="pipeline-deploy-status error">
          ❌ {deployError}
        </div>
      )}

      {deployStatus === 'success' && (
        <div className="pipeline-deploy-status success">
          ✅ Deployment successful!
          {deployOutput && (
            <DeployOutputs outputs={deployOutput} />
          )}
        </div>
      )}
    </PipelineShell>
  );
}

function DeployOutputs({ outputs }) {
  if (!outputs || typeof outputs !== 'object') return null;
  const entries = Object.entries(outputs).filter(([, v]) => v);
  if (!entries.length) return null;

  return (
    <table className="pipeline-tech-table" style={{ marginTop: 12, background: '#fff', borderRadius: 6, overflow: 'hidden' }}>
      <thead>
        <tr><th>Resource</th><th>Value</th></tr>
      </thead>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{k}</td>
            <td>
              {typeof v === 'string' && (v.startsWith('http') || v.startsWith('arn')) ? (
                <a href={v.startsWith('http') ? v : '#'} target="_blank" rel="noreferrer"
                   style={{ color: '#1d4ed8', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                  {v}
                </a>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{String(v)}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default DeployStage;
